import { Plus, Trash } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  emptyOrderItemRow,
  orderItemSubtotal,
  type OrderItemRow,
} from './orders-payload';
import { formatNumber } from './orders-format';

/**
 * Editor de las líneas del pedido (`productos`, obligatorio: al menos una).
 *
 * Cada fila es un producto con su cantidad y su precio unitario, y el subtotal se muestra calculado
 * (no se escribe): es el número que el backend guarda en el JSON del pedido y el que el bot le
 * dictó al cliente. Dejar la columna calculada evita que el admin escriba un subtotal que no cuadre
 * con `cantidad × precio`.
 *
 * El precio es opcional (una orden cargada a mano puede nacer sin precios y arreglarse con el campo
 * del total), pero si se escribe, el subtotal de la fila viaja al backend. Siempre queda al menos
 * una fila: si se borra la última, se repone una vacía.
 */

export interface OrderItemsFieldsProps {
  rows: OrderItemRow[];
  onChange: (rows: OrderItemRow[]) => void;
  disabled?: boolean;
  className?: string;
}

export function OrderItemsFields({
  rows,
  onChange,
  disabled = false,
  className,
}: OrderItemsFieldsProps) {
  function updateRow(id: string, patch: Partial<OrderItemRow>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function removeRow(id: string) {
    const next = rows.filter((row) => row.id !== id);
    onChange(next.length > 0 ? next : [emptyOrderItemRow()]);
  }

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center gap-2">
        <span className="flex-1 text-caption font-medium text-ink-soft">Producto</span>
        <span className="w-20 text-caption font-medium text-ink-soft">Cantidad</span>
        <span className="w-28 text-caption font-medium text-ink-soft">Precio unitario</span>
        <span className="w-24 text-right text-caption font-medium text-ink-soft">Subtotal</span>
        {/* Espacio del botón de eliminar, para que las columnas queden alineadas. */}
        <span className="w-9" aria-hidden="true" />
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => {
          const subtotal = orderItemSubtotal(row);

          return (
            <div key={row.id} className="flex items-center gap-2">
              <Input
                value={row.nombre}
                placeholder="ej. Blusa lino"
                disabled={disabled}
                aria-label={`Producto, línea ${index + 1}`}
                onChange={(event) => updateRow(row.id, { nombre: event.target.value })}
              />

              <Input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                className="w-20"
                value={row.cantidad}
                placeholder="1"
                disabled={disabled}
                aria-label={`Cantidad, línea ${index + 1}`}
                onChange={(event) => updateRow(row.id, { cantidad: event.target.value })}
              />

              <Input
                type="number"
                min={0}
                step="0.01"
                inputMode="decimal"
                className="w-28"
                value={row.precioUnitario}
                placeholder="ej. 45000"
                disabled={disabled}
                aria-label={`Precio unitario, línea ${index + 1}`}
                onChange={(event) => updateRow(row.id, { precioUnitario: event.target.value })}
              />

              <span className="w-24 text-right text-body tabular text-ink-soft">
                {subtotal === null ? '—' : formatNumber(subtotal)}
              </span>

              <Button
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={`Eliminar la línea ${index + 1}`}
                onClick={() => removeRow(row.id)}
              >
                <Trash size={16} />
              </Button>
            </div>
          );
        })}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        disabled={disabled}
        leftIcon={<Plus size={14} />}
        onClick={() => onChange([...rows, emptyOrderItemRow()])}
      >
        Agregar producto
      </Button>
    </div>
  );
}
